'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';

/**
 * Which document an attachment is. `dokumenPabean` is the declaration draft
 * (PIB on an import, PEB on an export) that every notification carries;
 * `respon` is the response document itself (SPPB, NPD, BILLING, ...).
 */
export type EmailResponseAttachmentKind = 'dokumenPabean' | 'respon';

/** One downloadable document on a response. */
export interface EmailResponseAttachment {
  index: number;
  kind: EmailResponseAttachmentKind;
  label: string;
  fileName: string;
  downloadUrl: string;
}

/** One bell item. */
export interface EmailResponseNotificationItem {
  id: number;
  nomorAju: string;
  namaPerusahaan: string;
  nomorPpjk: string;
  kodeRespon: string;
  nomorRespon: string;
  uraianSingkatRespon: string;
  waktuRespon: string;
  createdAt: string;
  attachments: EmailResponseAttachment[];
  /** NPD and SPTNP: responses with a deadline, flagged by the server. */
  isUrgent: boolean;
  isRead: boolean;
  readAt: string | null;
}

interface EmailResponseNotificationList {
  items: EmailResponseNotificationItem[];
  total: number;
  unreadCount: number;
}

export const EMAIL_RESPONSE_NOTIFICATIONS_KEY = ['email-response-notifications'] as const;

const PAGE_SIZE = 20;

const EMPTY_LIST: EmailResponseNotificationList = { items: [], total: 0, unreadCount: 0 };

async function fetchNotifications(signal: AbortSignal): Promise<EmailResponseNotificationList> {
  const { data } = await axiosInstance.get<{ data: EmailResponseNotificationList | null }>(
    apiUrl.notifications.emailResponses({ limit: PAGE_SIZE }),
    { signal }
  );

  const payload = data?.data;
  if (!payload) return EMPTY_LIST;

  return {
    items: payload.items ?? [],
    total: payload.total ?? 0,
    unreadCount: payload.unreadCount ?? 0,
  };
}

/**
 * Dev-only scaffolding.
 *
 * The bell has nothing to show until a real CEISA response lands for the
 * logged-in forwarder, which makes the badge, the unread tint and the
 * attachment row impossible to work on locally. While this is on, a couple of
 * fake unread items are folded into whatever the server returned; they are
 * never requested from or written back to the API.
 *
 * Three of them, deliberately: an ordinary SPPB, an urgent NPD, and one with
 * no response document at all, so the per-status colouring, the urgent badge
 * and both the two-PDF and one-PDF attachment rows are all visible.
 *
 * Set this to false (or make a production build, where it is already false) to
 * get the plain server-backed list back.
 */
const MOCK_UNREAD_NOTIFICATION = import.meta.env.DEV;

const MOCK_NOW = Date.now();

/**
 * Ids are negative so they can never collide with a real row, which is also
 * how the read and download paths recognise a mock item.
 */
const isMockNotificationId = (id: number): boolean => MOCK_UNREAD_NOTIFICATION && id < 0;

const MOCK_NOTIFICATIONS: EmailResponseNotificationItem[] = [
  {
    id: -1,
    nomorAju: '000020029326202510170056',
    namaPerusahaan: 'PT Contoh Importir Nusantara',
    nomorPpjk: '000000000000000',
    kodeRespon: '2010',
    nomorRespon: 'SPPB-000123/2026',
    uraianSingkatRespon: 'SPPB',
    waktuRespon: new Date(MOCK_NOW - 74 * 60 * 1000).toISOString(),
    createdAt: new Date(MOCK_NOW - 74 * 60 * 1000).toISOString(),
    // The ordinary shape: the declaration draft plus the response.
    attachments: [
      { index: 0, kind: 'dokumenPabean', label: 'PIB', fileName: 'PIB-contoh.pdf', downloadUrl: '' },
      { index: 1, kind: 'respon', label: 'SPPB', fileName: 'SPPB-contoh.pdf', downloadUrl: '' },
    ],
    isUrgent: false,
    isRead: false,
    readAt: null,
  },
  {
    id: -2,
    nomorAju: '000020029326202510160041',
    namaPerusahaan: 'PT Samudera Logistik Contoh',
    nomorPpjk: '000000000000000',
    kodeRespon: '2005',
    nomorRespon: 'NPD-000456/2026',
    uraianSingkatRespon: 'NPD',
    // Two hours behind the first so the list reads in a plausible order.
    waktuRespon: new Date(MOCK_NOW - 3 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(MOCK_NOW - 3 * 60 * 60 * 1000).toISOString(),
    attachments: [
      { index: 0, kind: 'dokumenPabean', label: 'PIB', fileName: 'PIB-contoh.pdf', downloadUrl: '' },
      { index: 1, kind: 'respon', label: 'NPD', fileName: 'NPD-contoh.pdf', downloadUrl: '' },
    ],
    isUrgent: true,
    isRead: false,
    readAt: null,
  },
  {
    // The floor case: nothing identifies a response document, so only the
    // declaration draft is offered.
    id: -3,
    nomorAju: '000030123456202501010001',
    namaPerusahaan: 'PT Ekspor Contoh Sejahtera',
    nomorPpjk: '000000000000000',
    kodeRespon: '',
    nomorRespon: '',
    uraianSingkatRespon: '',
    waktuRespon: new Date(MOCK_NOW - 26 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(MOCK_NOW - 26 * 60 * 60 * 1000).toISOString(),
    attachments: [{ index: 0, kind: 'dokumenPabean', label: 'PEB', fileName: 'PEB-contoh.pdf', downloadUrl: '' }],
    isUrgent: false,
    isRead: false,
    readAt: null,
  },
];

/**
 * CEISA sends `waktuRespon` as free-form text and it is occasionally absent,
 * so every read of it goes through here: the row's own response time when it
 * parses, the row's insert time otherwise, and null when neither is usable.
 *
 * Returns epoch milliseconds so callers can compare without allocating.
 */
export const parseResponseTime = (
  item: Pick<EmailResponseNotificationItem, 'waktuRespon' | 'createdAt'>
): number | null => {
  for (const candidate of [item.waktuRespon, item.createdAt]) {
    if (!candidate) continue;
    const parsed = new Date(candidate).getTime();
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
};

/**
 * The WebSocket message as the Go broadcast sites build it. Field names are
 * snake_case there and camelCase in the REST payload, and a couple of keys
 * were named differently before this feature existed (`aju_no`,
 * `uraian_respon`), so every spelling a live server can send is accepted here
 * rather than at each render site.
 */
interface SocketResponsePayload {
  id?: number | string;
  aju_no?: string;
  nomor_aju?: string;
  nomorAju?: string;
  company_name?: string;
  nama_perusahaan?: string;
  namaPerusahaan?: string;
  ppjk_npwp?: string;
  nomor_ppjk?: string;
  nomorPpjk?: string;
  kode_respon?: string;
  kodeRespon?: string;
  nomor_respon?: string;
  nomorRespon?: string;
  uraian_respon?: string;
  uraian_singkat_respon?: string;
  uraianSingkatRespon?: string;
  waktu_respon?: string;
  waktuRespon?: string;
  created_at?: string;
  createdAt?: string;
  attachments?: EmailResponseAttachment[];
  is_urgent?: boolean;
  isUrgent?: boolean;
  /** Present on `new_riwayat_respon`, which reports a batch for one AJU. */
  notifications?: SocketResponsePayload[];
}

/**
 * A message with no row id is dropped: without one the item could not be
 * marked read or have its documents downloaded, and the refetch that follows
 * every socket event brings in the real row anyway.
 */
const normalizeSocketItem = (raw: SocketResponsePayload): EmailResponseNotificationItem | null => {
  const id = Number(raw?.id ?? 0);
  if (!Number.isFinite(id) || id <= 0) return null;

  return {
    id,
    nomorAju: raw.nomor_aju ?? raw.aju_no ?? raw.nomorAju ?? '',
    namaPerusahaan: raw.nama_perusahaan ?? raw.company_name ?? raw.namaPerusahaan ?? '',
    nomorPpjk: raw.ppjk_npwp ?? raw.nomor_ppjk ?? raw.nomorPpjk ?? '',
    kodeRespon: raw.kode_respon ?? raw.kodeRespon ?? '',
    nomorRespon: raw.nomor_respon ?? raw.nomorRespon ?? '',
    uraianSingkatRespon: raw.uraian_singkat_respon ?? raw.uraian_respon ?? raw.uraianSingkatRespon ?? '',
    waktuRespon: raw.waktu_respon ?? raw.waktuRespon ?? '',
    createdAt: raw.created_at ?? raw.createdAt ?? '',
    attachments: Array.isArray(raw.attachments) ? raw.attachments : [],
    isUrgent: raw.is_urgent ?? raw.isUrgent ?? false,
    isRead: false,
    readAt: null,
  };
};

/**
 * A single socket message can describe one response
 * (`new_riwayat_respon_email`) or a batch of them (`new_riwayat_respon`, which
 * carries the post-terminal statuses found for one AJU).
 */
const extractSocketItems = (detail: SocketResponsePayload | null | undefined): EmailResponseNotificationItem[] => {
  if (!detail) return [];

  const raw = Array.isArray(detail.notifications) ? detail.notifications : [detail];
  return raw.map(normalizeSocketItem).filter((item): item is EmailResponseNotificationItem => item !== null);
};

/**
 * Backs the dashboard header bell.
 *
 * The list itself is server state, so it lives in React Query. Live updates
 * arrive over the CEISA WebSocket the dashboard already holds open — this hook
 * listens to the `ceisa-riwayat-respon-update` window event that
 * `useCeisaWebSocket` dispatches rather than opening a second socket. Each
 * event is applied to the cache immediately (so the badge and the list react
 * at once) and then reconciled with a refetch, which is what fills in read
 * state and any fields the broadcast did not carry.
 */
export default function useEmailResponseNotifications() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: EMAIL_RESPONSE_NOTIFICATIONS_KEY,
    queryFn: ({ signal }) => fetchNotifications(signal),
    // The socket is the real-time path; this is the safety net for a dropped
    // connection or a tab that was asleep.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  // Local read state for the dev mock items, which have no server rows to POST
  // to. Held as a plain array of ids so the memo below has a stable dependency.
  const [mockReadIds, setMockReadIds] = useState<number[]>([]);

  const list = useMemo<EmailResponseNotificationList>(() => {
    const server = data ?? EMPTY_LIST;
    if (!MOCK_UNREAD_NOTIFICATION) return server;

    const mocks = MOCK_NOTIFICATIONS.map((item) => {
      const isRead = mockReadIds.includes(item.id);
      return { ...item, isRead, readAt: isRead ? item.createdAt : null };
    });

    return {
      items: [...mocks, ...server.items].slice(0, PAGE_SIZE),
      total: server.total + mocks.length,
      unreadCount: server.unreadCount + mocks.filter((item) => !item.isRead).length,
    };
  }, [data, mockReadIds]);

  /**
   * When the most recent response landed, for the header's elapsed timer.
   *
   * Taken as the maximum rather than the first item's time: socket messages
   * are prepended as they arrive and the list is not re-sorted, so "first" is
   * not reliably "newest". `waktuRespon` is CEISA's own timestamp and is what
   * the rows show; `createdAt` is the fallback for a row whose CEISA time is
   * missing or unparseable.
   */
  const lastResponseAt = useMemo<Date | null>(() => {
    let newest: number | null = null;

    for (const item of list.items) {
      const at = parseResponseTime(item);
      if (at !== null && (newest === null || at > newest)) newest = at;
    }

    return newest === null ? null : new Date(newest);
  }, [list.items]);

  const applySocketItems = useCallback(
    (incoming: EmailResponseNotificationItem[]) => {
      if (incoming.length === 0) return;

      queryClient.setQueryData<EmailResponseNotificationList>(EMAIL_RESPONSE_NOTIFICATIONS_KEY, (previous) => {
        const current = previous ?? EMPTY_LIST;

        // A response can be broadcast more than once (the cron and the webhook
        // can both see it), so dedupe on id instead of blindly prepending.
        const known = new Set(current.items.map((item) => item.id));
        const fresh = incoming.filter((item) => !known.has(item.id));
        if (fresh.length === 0) return current;

        return {
          items: [...fresh, ...current.items].slice(0, PAGE_SIZE),
          total: current.total + fresh.length,
          unreadCount: current.unreadCount + fresh.length,
        };
      });
    },
    [queryClient]
  );

  useEffect(() => {
    const handleRiwayatResponUpdate = (event: Event) => {
      const detail = (event as CustomEvent<SocketResponsePayload>).detail;
      applySocketItems(extractSocketItems(detail));

      // Reconcile with the server: the broadcast is a notification, not the
      // record of truth, and a message we could not use still means the list
      // changed.
      queryClient.invalidateQueries({ queryKey: EMAIL_RESPONSE_NOTIFICATIONS_KEY });
    };

    window.addEventListener('ceisa-riwayat-respon-update', handleRiwayatResponUpdate);
    return () => window.removeEventListener('ceisa-riwayat-respon-update', handleRiwayatResponUpdate);
  }, [applySocketItems, queryClient]);

  const markReadMutation = useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.post(apiUrl.notifications.emailResponseRead(id));
      return id;
    },
    onSuccess: (id) => {
      queryClient.setQueryData<EmailResponseNotificationList>(EMAIL_RESPONSE_NOTIFICATIONS_KEY, (previous) => {
        if (!previous) return previous;
        const wasUnread = previous.items.some((item) => item.id === id && !item.isRead);
        return {
          ...previous,
          items: previous.items.map((item) => (item.id === id ? { ...item, isRead: true } : item)),
          unreadCount: wasUnread ? Math.max(0, previous.unreadCount - 1) : previous.unreadCount,
        };
      });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await axiosInstance.post(apiUrl.notifications.emailResponsesReadAll);
    },
    onSuccess: () => {
      queryClient.setQueryData<EmailResponseNotificationList>(EMAIL_RESPONSE_NOTIFICATIONS_KEY, (previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          items: previous.items.map((item) => ({ ...item, isRead: true })),
          unreadCount: 0,
        };
      });
    },
    onError: () => {
      toast.error('Error', { description: 'Gagal menandai semua notifikasi terbaca' });
    },
  });

  /**
   * Downloads one of a response's documents. The file is fetched through the
   * authenticated axios instance rather than linked to directly, because the
   * endpoint needs the bearer token and resolves the forwarder from the
   * session.
   */
  /**
   * Fetches one attachment and hands it to the browser.
   *
   * Resolves to whether the file actually reached the user, so the button that
   * started the download can show the outcome in place rather than leaving the
   * toast as the only evidence anything happened.
   */
  const downloadAttachment = useCallback(
    async (notificationId: number, attachment: EmailResponseAttachment): Promise<boolean> => {
      // The dev mocks have no stored file behind them; a real request would 404.
      if (isMockNotificationId(notificationId)) {
        toast.info('Mock', { description: 'Notifikasi contoh — tidak ada PDF untuk diunduh' });
        return false;
      }

      try {
        const response = await axiosInstance.get(
          apiUrl.notifications.emailResponseAttachment(notificationId, attachment.index),
          { responseType: 'blob' }
        );

        const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', attachment.fileName || `respon-${notificationId}-${attachment.index + 1}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);

        toast.success('Success', { description: 'PDF berhasil diunduh' });
        return true;
      } catch (error) {
        console.error('Failed to download response PDF:', error);
        toast.error('Error', { description: 'Gagal mengunduh PDF respon' });
        return false;
      }
    },
    []
  );

  // Mock items are read locally rather than through the API, which has no rows
  // to mark. Both entry points fall through to the real mutation for
  // everything else.
  const markRead = useCallback(
    (id: number) => {
      if (isMockNotificationId(id)) {
        setMockReadIds((previous) => (previous.includes(id) ? previous : [...previous, id]));
        return;
      }
      markReadMutation.mutate(id);
    },
    [markReadMutation.mutate]
  );

  const markAllRead = useCallback(() => {
    if (MOCK_UNREAD_NOTIFICATION) setMockReadIds(MOCK_NOTIFICATIONS.map((item) => item.id));
    markAllReadMutation.mutate();
  }, [markAllReadMutation.mutate]);

  return {
    items: list.items,
    total: list.total,
    unreadCount: list.unreadCount,
    hasUnread: list.unreadCount > 0,
    lastResponseAt,
    isLoading,
    isError,
    refetch,
    markRead,
    markAllRead,
    isMarkingAllRead: markAllReadMutation.isPending,
    downloadAttachment,
  };
}
